import { useState } from 'react';
import classnames from 'classnames';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, Size } from '../../../components/Button';
import { isValidEmail } from '../../../components/Question/Email';

import * as api from '../../../api';
import * as logger from '../../../logger';
import * as store from '../../../reducers/store';

import greenCheckCircle from '../../../assets/green-check-circle.svg';
import styles from './index.module.css';
import { getSelectedPracticePreference } from '../../../utils/formSet';

interface Props
  extends Pick<Modal.Props, 'onRequestClose' | 'isOpen'>,
    StateProps {
  selectedFormSetId: string;
  onRequestClose?: () => void;
  showReceipt?: boolean; // overrides practice preference
  emailFromBilling?: string;
}

interface StateProps {
  enablePaymentReceipts: boolean;
}

const PaymentSuccessModal: React.FC<Props> = ({
  enablePaymentReceipts,
  emailFromBilling,
  showReceipt,
  selectedFormSetId,
  ...props
}) => {
  const { t } = useTranslation();
  const { authenticationCode } = useParams<{ authenticationCode: string }>();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const closeWithReceiptRequest = async (sendReceipt: boolean) => {
    setSubmitting(true);
    try {
      if (sendReceipt) {
        await api.client.Payment.updatePaymentReceiptRequest({
          authenticationCode,
          previsitFormSetId: selectedFormSetId,
          sendReceipt,
          email: email || emailFromBilling || undefined,
        });
      }
    } catch (err) {
      logger.error(
        err,
        `[PaymentSuccessModal] [closeWithReceiptRequest]: errored while updating receipt status`,
      );
    } finally {
      setSubmitting(false);
    }

    props.onRequestClose && props.onRequestClose();
  };

  const showReceiptDialog =
    showReceipt !== undefined ? showReceipt : enablePaymentReceipts;

  return (
    <Modal
      appElement={document.getElementById('root') ?? undefined}
      shouldCloseOnOverlayClick={false}
      className={styles.ModalContent}
      overlayClassName={styles.Overlay}
      shouldCloseOnEsc={false}
      {...props}
    >
      <img alt="success" className={styles.Success} src={greenCheckCircle} />

      <div className={styles.ModalTitle}>{t('Payment successful')}</div>
      <div>{t('Thank you.')}</div>
      {!showReceiptDialog && (
        <Button
          onClick={props.onRequestClose}
          className={classnames(styles.ModalContinue, styles.MarginTop)}
          size={Size.Small}
          invertColors={false}
        >
          {t('Continue')}
        </Button>
      )}
      {showReceiptDialog && !emailFromBilling && (
        <>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            type="string"
            placeholder={t('Enter your email')}
          />
          <Button
            onClick={() => closeWithReceiptRequest(true)}
            className={styles.ModalContinue}
            size={Size.Small}
            disabled={submitting || !email || !isValidEmail(email)}
          >
            {t('Continue')}
          </Button>
          <Button
            onClick={() => closeWithReceiptRequest(false)}
            className={classnames(styles.ModalContinue, styles.NoBorder)}
            size={Size.Small}
            invertColors={true}
            disabled={submitting}
          >
            {t('Continue without receipt')}
          </Button>
        </>
      )}
      {showReceiptDialog && emailFromBilling && (
        <>
          <Button
            onClick={() => closeWithReceiptRequest(true)}
            className={styles.ModalContinue}
            size={Size.Small}
            disabled={submitting}
          >
            {t('Email me a receipt')}
          </Button>
          <Button
            onClick={() => closeWithReceiptRequest(false)}
            className={styles.ModalContinue}
            size={Size.Small}
            invertColors={true}
            disabled={submitting}
          >
            {t('Continue without receipt')}
          </Button>
        </>
      )}
    </Modal>
  );
};

const mapStateToProps = (store: store.FormSetSelected): StateProps => ({
  enablePaymentReceipts:
    getSelectedPracticePreference(store).enablePaymentReceipts,
});

export default connect(mapStateToProps)(PaymentSuccessModal);
